import React, { useEffect, useState } from 'react'

import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'

import Context from './Context'

import { refreshToken as performRefreshToken } from '@redux/identity'
import { fetchProfile } from '@redux/user'
import { setUserToken } from '@utils/common'
import { status } from '@utils/redux'
import { buildLoginURL, isNeedSilentLogin, userToken } from '@utils/identity'
import storage from '@utils/storage'

export default ({ children }) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const userState = useSelector((state) => state.user)

  const [isAuthenticated, setIsAuthenticated] = useState(null)
  const [user, setUser] = useState({})
  const [accessToken, setAccessToken] = useState(null)
  const [refreshToken, setRefreshToken] = useState(null)
  const [accessTokenExpires, setAccessTokenExpires] = useState(
    storage.get('accessTokenExpires') || 0
  )
  const [refreshTokenExpires, setRefreshTokenExpires] = useState(0)

  const {
    query: { country, userToken: newUserToken },
    pathname
  } = router

  const refreshTokenInterval = (accessTokenExpires - 60) * 1000
  const isCallbackPage = pathname.includes('callback')

  useEffect(() => {
    if (newUserToken) {
      if (newUserToken.includes('Bearer')) {
        storage.set('accessToken', newUserToken.replace('Bearer ', ''))
      } else {
        setUserToken(newUserToken)
      }
      router.replace(`/${country}/`)
    }
  }, [newUserToken])

  useEffect(() => {
    if (userState.status === status.LOADED) {
      setIsAuthenticated(true)
      setUser(userState.data)
    }
  }, [userState.status])

  useEffect(() => {
    const updateAccessToken = (accessToken) => {
      if (!accessToken.includes('Bearer')) {
        return false
      }

      const token = accessToken?.replace('Bearer ', '')

      setAccessToken(token)
      storage.set('accessToken', token)
    }

    window.setAccessToken = updateAccessToken
  }, [])

  useEffect(() => {
    const authenticate = async () => {
      try {
        await dispatch(performRefreshToken())
          .then((payload) => {
            setAccessToken(payload.access_token)
            setRefreshToken(payload.refresh_token)
            setAccessTokenExpires(payload.expires_in)
            setRefreshTokenExpires(payload.refresh_expires_in)
          })
          .catch((e) => null)

        if (!userToken) {
          return setIsAuthenticated(false)
        }

        const userAction = await dispatch(fetchProfile())
        setUser(userAction.payload.data)
        setIsAuthenticated(true)
      } catch (error) {
        setIsAuthenticated(false)
      }
    }

    if (!isAuthenticated && userState.status === status.PRISTINE) {
      authenticate()
    }
  }, [])

  useEffect(() => {
    if (refreshTokenInterval < 0) {
      return false
    }

    window.setInterval(() => {
      dispatch(performRefreshToken())
        .then((payload) => {
          setAccessToken(payload.access_token)
          setRefreshToken(payload.refresh_token)
          setAccessTokenExpires(payload.expires_in)
          setRefreshTokenExpires(payload.refresh_expires_in)
        })
        .catch((e) => {
          setAccessToken(null)
          setRefreshToken(null)
          setAccessTokenExpires(null)
          setRefreshTokenExpires(null)
        })
    }, refreshTokenInterval)
  }, [])

  useEffect(() => {
    window.addEventListener(
      'message',
      (event) => {
        if (event.data.action !== 'login') {
          return false
        }

        const refreshProfile = async () => {
          const userAction = await dispatch(fetchProfile())
          setUser(userAction.payload.data)
        }

        const {
          data: { payload }
        } = event

        setIsAuthenticated(true)
        setAccessToken(payload.access_token)
        setRefreshToken(payload.refresh_token)
        setAccessTokenExpires(payload.expires_in)
        setRefreshTokenExpires(payload.refresh_expires_in)

        refreshProfile()
      },
      false
    )
  }, [])

  const renderSilentLogin = () => {
    if (!isNeedSilentLogin || isAuthenticated || isCallbackPage) {
      return null
    }

    return (
      <iframe src={buildLoginURL({ silent: true })} width={0} height={0} />
    )
  }

  return (
    <Context.Provider
      value={{
        accessToken,
        refreshToken,
        accessTokenExpires,
        refreshTokenExpires,
        isAuthenticated,
        user
      }}
    >
      <>
        {children}
        {renderSilentLogin()}
      </>
    </Context.Provider>
  )
}
